import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw, Play, ChevronDown, ChevronRight, Clock, CheckCircle,
  XCircle, Loader2, ExternalLink, User
} from 'lucide-react';

export function JobQueueContent() {
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [jobRuns, setJobRuns] = useState({});
  const [loadingRuns, setLoadingRuns] = useState({});
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [triggeringCustomer, setTriggeringCustomer] = useState(null);

  const fetchJobTypes = useCallback(async () => {
    try {
      const { data } = await api.get('/jobs/types');
      setJobTypes(data);
    } catch (err) {
      console.error('Failed to fetch job types:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobTypes(); }, [fetchJobTypes]);

  const fetchRuns = async (jobId) => {
    setLoadingRuns(prev => ({ ...prev, [jobId]: true }));
    try {
      const { data } = await api.get(`/jobs/${jobId}/runs?limit=10`);
      setJobRuns(prev => ({ ...prev, [jobId]: data }));
    } catch (err) {
      console.error('Failed to fetch runs:', err);
    } finally {
      setLoadingRuns(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const toggleExpand = (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
    } else {
      setExpandedJob(jobId);
      if (!jobRuns[jobId]) fetchRuns(jobId);
    }
  };

  const triggerJob = async (jobId) => {
    setTriggeringJob(jobId);
    try {
      await api.post(`/jobs/${jobId}/trigger`);
      toast.success('Job triggered successfully');
      setTimeout(() => { fetchJobTypes(); fetchRuns(jobId); }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggeringJob(null);
    }
  };

  const triggerJobForCustomer = async (jobId, accountId) => {
    setTriggeringCustomer(`${jobId}-${accountId}`);
    try {
      await api.post(`/jobs/${jobId}/trigger/${accountId}`);
      toast.success('Job triggered for customer');
      setTimeout(() => fetchRuns(jobId), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger job');
    } finally {
      setTriggeringCustomer(null);
    }
  };

  const openRunLog = (runId) => {
    window.open(`/job-run/${runId}`, '_blank');
  };

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (status === 'failed') return <XCircle className="h-4 w-4 text-red-600" />;
    if (status === 'running') return <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />;
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    const d = new Date(isoStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="job-queue-title">Job Queue</h1>
          <p className="text-sm text-muted-foreground">Manage and monitor scheduled jobs and data sync tasks</p>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchJobTypes} data-testid="refresh-jobs-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

        <div className="border rounded-none divide-y">
          {jobTypes.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">No jobs registered</div>
          ) : (
            jobTypes.map(job => (
              <div key={job.id} data-testid={`job-type-${job.id}`}>
                {/* Job Type Header */}
                <div
                  className="flex items-center justify-between p-4 hover:bg-muted/30 cursor-pointer"
                  onClick={() => toggleExpand(job.id)}
                  data-testid={`job-type-header-${job.id}`}
                >
                  <div className="flex items-center gap-3">
                    {expandedJob === job.id ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <div className="font-medium">{job.name}</div>
                      <div className="text-xs text-muted-foreground">{job.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-xs">
                      <div className="text-muted-foreground">Last run: {formatTime(job.last_run_at)}</div>
                      <div className="flex items-center gap-2 justify-end mt-0.5">
                        {job.last_run_status && (
                          <Badge
                            variant={job.last_run_status === 'completed' ? 'default' : 'destructive'}
                            className="rounded-none text-[10px] px-1.5 py-0"
                          >
                            {job.last_run_status}
                          </Badge>
                        )}
                        <span className="text-muted-foreground">
                          {job.total_runs} runs · {job.success_rate}% success
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {job.schedule}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none"
                      disabled={triggeringJob === job.id}
                      onClick={(e) => { e.stopPropagation(); triggerJob(job.id); }}
                      data-testid={`trigger-job-${job.id}`}
                    >
                      {triggeringJob === job.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                      ) : (
                        <Play className="h-3.5 w-3.5 mr-1" />
                      )}
                      Trigger
                    </Button>
                  </div>
                </div>

                {/* Expanded Runs Accordion */}
                {expandedJob === job.id && (
                  <div className="bg-muted/10 border-t">
                    {loadingRuns[job.id] ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : !jobRuns[job.id] || jobRuns[job.id].length === 0 ? (
                      <div className="text-center py-6 text-xs text-muted-foreground">No runs yet</div>
                    ) : (
                      <div className="divide-y">
                        {jobRuns[job.id].map(run => (
                          <RunAccordion
                            key={run.id}
                            run={run}
                            jobId={job.id}
                            triggeringCustomer={triggeringCustomer}
                            onTriggerCustomer={triggerJobForCustomer}
                            onOpenLog={openRunLog}
                            statusIcon={statusIcon}
                            formatTime={formatTime}
                          />
                        ))}
                      </div>
                    )}
                    <div className="p-2 text-center">
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => fetchRuns(job.id)}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Refresh Runs
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
    </div>
  );
}

function RunAccordion({ run, jobId, triggeringCustomer, onTriggerCustomer, onOpenLog, statusIcon, formatTime }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-l-2 border-l-transparent hover:border-l-primary/30" data-testid={`job-run-${run.id}`}>
      <div
        className="flex items-center justify-between px-6 py-3 hover:bg-muted/30 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {statusIcon(run.status)}
          <div>
            <span className="text-sm font-medium">{formatTime(run.started_at)}</span>
            {run.duration_sec > 0 && (
              <span className="text-xs text-muted-foreground ml-2">({run.duration_sec}s)</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {run.triggered_by_name && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <User className="h-3 w-3" /> {run.triggered_by_name}
            </span>
          )}
          {run.totals && (
            <span className="text-xs text-muted-foreground">
              {Object.entries(run.totals).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ')}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={(e) => { e.stopPropagation(); onOpenLog(run.id); }}
            data-testid={`view-run-log-${run.id}`}
          >
            <ExternalLink className="h-3 w-3 mr-1" /> Log
          </Button>
        </div>
      </div>

      {expanded && run.customer_results && run.customer_results.length > 0 && (
        <div className="px-8 pb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-1.5 font-medium">Customer</th>
                <th className="text-left py-1.5 font-medium">Result</th>
                <th className="text-left py-1.5 font-medium">Errors</th>
                <th className="text-right py-1.5 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {run.customer_results.map((cr, i) => (
                <tr key={i} className="border-b border-dashed last:border-0">
                  <td className="py-1.5">{cr.customer_name || cr.account_id?.slice(0, 8)}</td>
                  <td className="py-1.5 text-muted-foreground">
                    {Object.entries(cr)
                      .filter(([k]) => !['customer_name', 'account_id', 'errors', 'mongo_account_id'].includes(k))
                      .map(([k, v]) => typeof v === 'number' ? `${k.replace(/_/g, ' ')}: ${v}` : null)
                      .filter(Boolean)
                      .join(', ')
                    }
                  </td>
                  <td className="py-1.5">
                    {cr.errors && cr.errors.length > 0 ? (
                      <Badge variant="destructive" className="rounded-none text-[10px]">{cr.errors.length} error(s)</Badge>
                    ) : (
                      <span className="text-green-600">OK</span>
                    )}
                  </td>
                  <td className="py-1.5 text-right">
                    {cr.account_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-[10px]"
                        disabled={triggeringCustomer === `${jobId}-${cr.account_id}`}
                        onClick={() => onTriggerCustomer(jobId, cr.account_id)}
                      >
                        {triggeringCustomer === `${jobId}-${cr.account_id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <><Play className="h-3 w-3 mr-0.5" /> Re-run</>
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {run.error && (
        <div className="px-8 pb-3">
          <div className="text-xs text-destructive bg-destructive/10 p-2 rounded-sm font-mono">{run.error}</div>
        </div>
      )}
    </div>
  );
}

export default function JobQueuePage() {
  return <JobQueueContent />;
}
